/**
 * React Query hook for workspace data
 *
 * Provides data fetching for a single workspace with caching support.
 */

import { useQuery } from '@tanstack/react-query';
import { workspaceApi } from '../services/workspaceApi';
import type { Workspace } from '../types/workspace';

/**
 * Query key factory for workspace
 */
export const workspaceKeys = {
  all: ['workspace'] as const,
  detail: (workspaceId: string) => ['workspace', workspaceId] as const,
};

/**
 * Hook return type for useWorkspace
 */
export interface UseWorkspaceResult {
  workspace: Workspace | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * React Query hook for fetching workspace data
 *
 * @param workspaceId - The workspace ID to fetch
 * @returns Workspace data, loading states, and refetch function
 *
 * @example
 * ```tsx
 * const { workspace, isLoading, error, refetch } = useWorkspace('workspace-123');
 * ```
 */
export function useWorkspace(workspaceId: string | undefined): UseWorkspaceResult {
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: workspaceKeys.detail(workspaceId ?? ''),
    queryFn: async () => {
      const response = await workspaceApi.getWorkspace(workspaceId!);
      return response;
    },
    enabled: workspaceId !== undefined,
  });

  const refetch = async () => {
    await queryRefetch();
  };

  return {
    workspace: data,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
  };
}
